import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPhone, FaEnvelope, FaWhatsapp, FaClock, FaCalendar, FaSave, FaEdit, FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import '../../Styles/CustomerCareManagement.css';
import { useSelector } from 'react-redux';
import {
  getCustomerCare,
  createCustomerCare,
  updateCustomerCare,
  deleteCustomerCare,
  toggleCustomerCareStatus
} from '../../Services/Operations/ProductAPI';

// Skeleton Components
const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonDetailCard = () => (
  <div className="detail-card skeleton">
    <div className="skeleton-icon"></div>
    <div className="skeleton-content">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
  </div>
);

const CustomerCareManagement = () => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [customerCare, setCustomerCare] = useState(null);
  const [error, setError] = useState(null);
  const { token } = useSelector(state => state.user);

  const [formData, setFormData] = useState({
    phoneNumbers: [''],
    emailAddresses: [''],
    whatsappNumbers: [''],
    workingHours: {
      start: '09:00',
      end: '18:00'
    },
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    supportEmail: '',
    responseTime: 'Within 24 hours',
    isActive: true
  });

  // Fetch customer care details
  const fetchCustomerCare = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getCustomerCare(token);

      if (response && response.success) {
        const data = response.data;
        setCustomerCare(data);
        // Update form data with fetched data
        setFormData({
          phoneNumbers: data.phoneNumbers || [''],
          emailAddresses: data.emailAddresses || [''],
          whatsappNumbers: data.whatsappNumbers || [''],
          workingHours: data.workingHours || { start: '09:00', end: '18:00' },
          workingDays: data.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          supportEmail: data.supportEmail || '',
          responseTime: data.responseTime || 'Within 24 hours',
          isActive: data.isActive !== undefined ? data.isActive : true
        });
      } else if (response && response.status === 404) {
        // No record found
        setCustomerCare(null);
        resetForm();
      } else {
        setError('Failed to fetch customer care details');
      }
    } catch (err) {
      console.error('Error fetching customer care:', err);
      if (err.response?.status === 404) {
        setCustomerCare(null);
        resetForm();
      } else {
        setError('Failed to fetch customer care details. Please try again.');
        toast.error('Failed to fetch customer care details');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCustomerCare();
    }
  }, [token]);

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      phoneNumbers: [''],
      emailAddresses: [''],
      whatsappNumbers: [''],
      workingHours: {
        start: '09:00',
        end: '18:00'
      },
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      supportEmail: '',
      responseTime: 'Within 24 hours',
      isActive: true
    });
  };

  // Handle input changes for arrays
  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  // Add new item to array
  const addArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };

  // Remove item from array
  const removeArrayItem = (field, index) => {
    if (formData[field].length <= 1) {
      toast.warning('At least one item is required');
      return;
    }
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  // Handle working hours change
  const handleWorkingHoursChange = (type, value) => {
    setFormData({
      ...formData,
      workingHours: {
        ...formData.workingHours,
        [type]: value
      }
    });
  };

  // Handle working days toggle
  const toggleWorkingDay = (day) => {
    const currentDays = formData.workingDays || [];
    if (currentDays.includes(day)) {
      setFormData({
        ...formData,
        workingDays: currentDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        workingDays: [...currentDays, day]
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone numbers
    const validPhoneNumbers = formData.phoneNumbers.filter(p => p.trim() !== '');
    if (validPhoneNumbers.length === 0) {
      toast.error('Please add at least one phone number');
      return;
    }

    // Validate phone number format (basic)
    const phoneRegex = /^[+\d\s-]{10,15}$/;
    for (const phone of validPhoneNumbers) {
      if (!phoneRegex.test(phone.trim())) {
        toast.error(`Invalid phone number format: ${phone}. Please use format like +91 9876543210`);
        return;
      }
    }

    // Filter out empty values
    const submitData = {
      ...formData,
      phoneNumbers: validPhoneNumbers,
      emailAddresses: formData.emailAddresses.filter(e => e.trim() !== ''),
      whatsappNumbers: formData.whatsappNumbers.filter(w => w.trim() !== ''),
      workingDays: formData.workingDays || []
    };

    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (customerCare) {
        // Update existing
        response = await updateCustomerCare(customerCare._id, submitData, token);
      } else {
        // Create new
        response = await createCustomerCare(submitData, token);
      }

      if (response && response.success) {
        toast.success(customerCare ? 'Customer care details updated successfully!' : 'Customer care details created successfully!');
        setCustomerCare(response.data);
        setIsEditing(false);
        fetchCustomerCare(); // Refresh data
      } else {
        throw new Error(response?.message || 'Failed to save customer care details');
      }
    } catch (err) {
      console.error('Error saving customer care:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save customer care details';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    if (customerCare) {
      setFormData({
        phoneNumbers: customerCare.phoneNumbers || [''],
        emailAddresses: customerCare.emailAddresses || [''],
        whatsappNumbers: customerCare.whatsappNumbers || [''],
        workingHours: customerCare.workingHours || { start: '09:00', end: '18:00' },
        workingDays: customerCare.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        supportEmail: customerCare.supportEmail || '',
        responseTime: customerCare.responseTime || 'Within 24 hours',
        isActive: customerCare.isActive !== undefined ? customerCare.isActive : true
      });
    } else {
      resetForm();
    }
    setIsEditing(false);
    setError(null);
  };

  // Handle delete customer care
  const handleDelete = async () => {
    if (!customerCare) return;
    
    if (window.confirm('Are you sure you want to delete customer care details?')) {
      try {
        setLoading(true);
        const response = await deleteCustomerCare(customerCare._id, token);
        if (response && response.success) {
          toast.success('Customer care details deleted successfully!');
          setCustomerCare(null);
          resetForm();
          fetchCustomerCare();
        } else {
          throw new Error(response?.message || 'Failed to delete customer care details');
        }
      } catch (err) {
        console.error('Error deleting customer care:', err);
        toast.error(err.message || 'Failed to delete customer care details');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle toggle status
  const handleToggleStatus = async () => {
    if (!customerCare) return;
    
    try {
      setLoading(true);
      const response = await toggleCustomerCareStatus(customerCare._id, token);
      if (response && response.success) {
        toast.success(`Customer care ${response.data.isActive ? 'activated' : 'deactivated'} successfully!`);
        setCustomerCare(response.data);
        setFormData(prev => ({
          ...prev,
          isActive: response.data.isActive
        }));
      } else {
        throw new Error(response?.message || 'Failed to toggle status');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error(err.message || 'Failed to toggle status');
    } finally {
      setLoading(false);
    }
  };

  // Render loading state
  if (loading && !customerCare && !isEditing) {
    return (
      <div className="customer-care-container">
        <div className="page-header">
          <h1>📞 Customer Care Management</h1>
          <p>Loading customer care details...</p>
        </div>
        <div className="content-card">
          <div className="loading-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <SkeletonDetailCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render view mode
  const renderViewMode = () => {
    if (!customerCare) {
      return (
        <div className="empty-state">
          <div className="empty-icon">📞</div>
          <h3>No Customer Care Details Found</h3>
          <p>Click the "Add Customer Care" button to create new details</p>
          <button className="btn-primary" onClick={() => setIsEditing(true)}>
            <FaPlus /> Add Customer Care
          </button>
        </div>
      );
    }

    return (
      <div className="customer-care-view">
        <div className="view-header">
          <h2>Customer Care Details</h2>
          <div className="header-actions">
            <button className="btn-edit" onClick={() => setIsEditing(true)}>
              <FaEdit /> Edit Details
            </button>
            <button className="btn-delete" onClick={handleDelete}>
              <FaTrash /> Delete
            </button>
            <button className={`btn-toggle ${customerCare.isActive ? 'active' : 'inactive'}`} onClick={handleToggleStatus}>
              {customerCare.isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="details-grid">
          <div className="detail-card">
            <div className="detail-icon"><FaPhone /></div>
            <div className="detail-content">
              <h4>Phone Numbers</h4>
              {customerCare.phoneNumbers?.map((phone, index) => (
                <p key={index}>{phone}</p>
              ))}
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon"><FaEnvelope /></div>
            <div className="detail-content">
              <h4>Email Addresses</h4>
              {customerCare.emailAddresses?.map((email, index) => (
                <p key={index}>{email}</p>
              ))}
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon"><FaWhatsapp /></div>
            <div className="detail-content">
              <h4>WhatsApp Numbers</h4>
              {customerCare.whatsappNumbers?.map((whatsapp, index) => (
                <p key={index}>{whatsapp}</p>
              ))}
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon"><FaClock /></div>
            <div className="detail-content">
              <h4>Working Hours</h4>
              <p>{customerCare.workingHours?.start} - {customerCare.workingHours?.end}</p>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon"><FaCalendar /></div>
            <div className="detail-content">
              <h4>Working Days</h4>
              <p>{customerCare.workingDays?.join(', ')}</p>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-icon"><FaEnvelope /></div>
            <div className="detail-content">
              <h4>Support Email</h4>
              <p>{customerCare.supportEmail || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-card full-width">
            <div className="detail-content">
              <h4>Response Time</h4>
              <p>{customerCare.responseTime || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-card full-width">
            <div className="detail-content">
              <h4>Status</h4>
              <span className={`status-badge ${customerCare.isActive ? 'active' : 'inactive'}`}>
                {customerCare.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render edit mode
  const renderEditMode = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <form onSubmit={handleSubmit} className="customer-care-form">
        <div className="form-header">
          <h2>{customerCare ? 'Edit Customer Care Details' : 'Add Customer Care Details'}</h2>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              <FaTimes /> Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              <FaSave /> {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-grid">
          {/* Phone Numbers */}
          <div className="form-group full-width">
            <label>Phone Numbers <span className="required">*</span></label>
            {formData.phoneNumbers.map((phone, index) => (
              <div key={index} className="array-input">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => handleArrayChange('phoneNumbers', index, e.target.value)}
                  placeholder="Enter phone number (e.g., +91 9876543210)"
                  required
                />
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeArrayItem('phoneNumbers', index)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() => addArrayItem('phoneNumbers')}
            >
              <FaPlus /> Add Phone Number
            </button>
          </div>

          {/* Email Addresses */}
          <div className="form-group full-width">
            <label>Email Addresses</label>
            {formData.emailAddresses.map((email, index) => (
              <div key={index} className="array-input">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleArrayChange('emailAddresses', index, e.target.value)}
                  placeholder="Enter email address"
                />
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeArrayItem('emailAddresses', index)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() => addArrayItem('emailAddresses')}
            >
              <FaPlus /> Add Email Address
            </button>
          </div>

          {/* WhatsApp Numbers */}
          <div className="form-group full-width">
            <label>WhatsApp Numbers</label>
            {formData.whatsappNumbers.map((whatsapp, index) => (
              <div key={index} className="array-input">
                <input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => handleArrayChange('whatsappNumbers', index, e.target.value)}
                  placeholder="Enter WhatsApp number"
                />
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeArrayItem('whatsappNumbers', index)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() => addArrayItem('whatsappNumbers')}
            >
              <FaPlus /> Add WhatsApp Number
            </button>
          </div>

          {/* Support Email */}
          <div className="form-group">
            <label>Support Email</label>
            <input
              type="email"
              value={formData.supportEmail}
              onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
              placeholder="Enter support email"
            />
          </div>

          {/* Response Time */}
          <div className="form-group">
            <label>Response Time</label>
            <input
              type="text"
              value={formData.responseTime}
              onChange={(e) => setFormData({ ...formData, responseTime: e.target.value })}
              placeholder="e.g., Within 24 hours"
            />
          </div>

          {/* Working Hours */}
          <div className="form-group">
            <label>Working Hours Start</label>
            <input
              type="time"
              value={formData.workingHours.start}
              onChange={(e) => handleWorkingHoursChange('start', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Working Hours End</label>
            <input
              type="time"
              value={formData.workingHours.end}
              onChange={(e) => handleWorkingHoursChange('end', e.target.value)}
            />
          </div>

          {/* Working Days */}
          <div className="form-group full-width">
            <label>Working Days</label>
            <div className="days-grid">
              {days.map((day) => (
                <label key={day} className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.workingDays?.includes(day)}
                    onChange={() => toggleWorkingDay(day)}
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="form-group full-width">
            <label>Status</label>
            <div className="status-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="toggle-slider"></span>
                {formData.isActive ? 'Active' : 'Inactive'}
              </label>
            </div>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className="customer-care-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h1>📞 Customer Care Management</h1>
        <p>Manage customer support contact details and working hours</p>
      </div>

      <div className="content-card">
        {isEditing ? renderEditMode() : renderViewMode()}
      </div>
    </div>
  );
};

export default CustomerCareManagement;